import { Info } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'

import type { WinnerImportSupply, WinnerImportSupplyCatalog } from './winner-import.api'
import {
  CREATE_SUPPLIER_VALUE, supplierChoiceOf, supplierValueOf, TO_SAY_VALUE, type SupplyChoice,
} from './winner-supply'

type Props = {
  supply: WinnerImportSupply
  choices: Record<string, SupplyChoice>
  onChange: (key: string, choice: SupplyChoice) => void
}

/**
 * SOLY-IMPORT-003 S12 — « Chez qui commander »: one question per Winner catalogue, remembered at the
 * creation so the next import asks nothing. A supplier is suggested by its name, never created
 * without the user choosing « Créer ».
 */
export function WinnerSupplyBlock({ supply, choices, onChange }: Props) {
  const noPurchase = supply.noPurchaseLines
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Une seule question par catalogue : au prochain import, Soly s&apos;en souviendra. La quantité à commander est la quantité vendue.
      </p>
      <ul className="divide-y rounded-lg border">
        {supply.catalogs.map((catalog) => (
          <CatalogRow
            key={catalog.key}
            catalog={catalog}
            suppliers={supply.suppliers}
            choice={choices[catalog.key] ?? { mode: 'buy', supplierId: null }}
            onChange={(choice) => onChange(catalog.key, choice)}
          />
        ))}
      </ul>
      {noPurchase.length > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border px-3.5 py-3 text-sm text-muted-foreground" data-testid="winner-import-supply-no-purchase">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            <b className="text-foreground">{noPurchase.length} ligne{noPurchase.length > 1 ? 's' : ''} sans achat</b>, mise{noPurchase.length > 1 ? 's' : ''} en
            « Aucun » sans question : {noPurchase.slice(0, 4).join(', ')}{noPurchase.length > 4 ? '…' : ''}. Les lignes que Winner marque
            « pas à commander » suivent la même règle.
          </span>
        </div>
      )}
    </div>
  )
}

const MODES = [
  { mode: 'buy', label: 'Acheter chez' },
  { mode: 'make', label: 'Fabriquer' },
  { mode: 'none', label: 'Aucun' },
] as const

function CatalogRow({ catalog, suppliers, choice, onChange }: {
  catalog: WinnerImportSupplyCatalog
  suppliers: { id: string; name: string }[]
  choice: SupplyChoice
  onChange: (choice: SupplyChoice) => void
}) {
  const buying = choice.mode === 'buy' || choice.mode === 'create'
  const createName = (choice.mode === 'create' && choice.name.trim()) || catalog.newSupplierName
  const hint = choice.mode === 'create'
    ? 'créé à la validation'
    : choice.mode === 'buy' && choice.supplierId && choice.supplierId === catalog.supplierId && catalog.kind === 'suggested'
      ? 'suggéré : même nom'
      : choice.mode === 'buy' && !choice.supplierId
        ? 'les lignes resteront « fournisseur à préciser »'
        : null

  return (
    <li className="grid gap-2 px-4 py-3 md:grid-cols-[minmax(0,1fr)_minmax(0,30rem)] md:items-center" data-testid={`winner-import-supply-row-${catalog.key}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium">{catalog.catalogName}</span>
          {catalog.kind === 'mapped' && <Badge variant="secondary">Déjà associé</Badge>}
        </div>
        <p className="text-xs text-muted-foreground">{catalog.lineCount} ligne{catalog.lineCount > 1 ? 's' : ''}</p>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label={`Appro pour ${catalog.catalogName}`}>
          {MODES.map((m) => {
            const active = m.mode === 'buy' ? buying : choice.mode === m.mode
            return (
              <Button
                key={m.mode}
                type="button"
                size="sm"
                variant={active ? 'default' : 'outline'}
                role="radio"
                aria-checked={active}
                onClick={() => onChange(m.mode === 'buy' ? (buying ? choice : { mode: 'buy', supplierId: catalog.supplierId }) : { mode: m.mode })}
                data-testid={`winner-import-supply-mode-${m.mode}-${catalog.key}`}
              >
                {m.label}
              </Button>
            )
          })}
          {buying && (
            <Select value={supplierValueOf(choice)} onValueChange={(value) => onChange(supplierChoiceOf(value, catalog, choice))}>
              <SelectTrigger
                className={choice.mode === 'buy' && !choice.supplierId ? 'h-8 w-56 border-amber-300 bg-amber-50 text-amber-900' : 'h-8 w-56'}
                data-testid={`winner-import-supply-supplier-${catalog.key}`}
                aria-label={`Fournisseur pour ${catalog.catalogName}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TO_SAY_VALUE}>Fournisseur à préciser</SelectItem>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
                <SelectSeparator />
                <SelectItem value={CREATE_SUPPLIER_VALUE}>{createName ? `+ Créer « ${createName} »` : '+ Créer un fournisseur'}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        {choice.mode === 'create' && (
          <Input
            value={choice.name}
            maxLength={200}
            onChange={(e) => onChange({ mode: 'create', name: e.target.value })}
            placeholder="Nom du nouveau fournisseur"
            aria-label={`Nom du nouveau fournisseur pour ${catalog.catalogName}`}
            data-testid={`winner-import-supply-new-name-${catalog.key}`}
          />
        )}
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    </li>
  )
}
